<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid
      :desktop-default-grid="[2, 20]"
      :tablet-default-grid="[1, 14]"
      :mobile-default-grid="[1, 14]"
    >
      <div class="c-workflow-rapid__inner">
        <JssRichText class="c-workflow-rapid__title" :field="fields?.title" tag="h2" />

        <div v-if="steps.length" class="c-workflow-rapid__timeline" :style="timelineStyle">
          <span class="c-workflow-rapid__timeline-line" aria-hidden="true"></span>
          <article
            v-for="(step, index) in steps"
            :key="step.id || step.fields.title?.value"
            class="c-workflow-rapid__step"
            :class="{ 'c-workflow-rapid__step--active': isStepActive(step, index) }"
          >
            <span class="c-workflow-rapid__dot" aria-hidden="true"></span>
            <h3 class="c-workflow-rapid__step-title">{{ step.fields.title.value }}</h3>
          </article>
        </div>

        <div class="c-workflow-rapid__cards">
          <article class="c-workflow-rapid__info-card">
            <h3 v-if="fields.cardTitle?.value" class="c-workflow-rapid__card-title">
              {{ fields.cardTitle.value }}
            </h3>

            <div
              v-if="fields.durationValue?.value || fields.durationUnit?.value"
              class="c-workflow-rapid__duration"
            >
              <strong v-if="fields.durationValue?.value" class="c-workflow-rapid__duration-value">
                {{ fields.durationValue.value }}
              </strong>
              <span v-if="fields.durationUnit?.value" class="c-workflow-rapid__duration-unit">
                {{ fields.durationUnit.value }}
              </span>
            </div>

            <RichText
              v-if="fields.bullets?.value"
              class="c-workflow-rapid__bullets"
              :field="fields.bullets"
            />
          </article>

          <article class="c-workflow-rapid__process-card">
            <div v-if="processItems.length" class="c-workflow-rapid__process-scroll">
              <div class="c-workflow-rapid__process-row">
                <template
                  v-for="(item, index) in processItems"
                  :key="item.id || item.fields.label?.value"
                >
                  <div class="c-workflow-rapid__process-item">
                    <Icon
                      v-if="getIconSvg(item)"
                      class="c-workflow-rapid__process-icon"
                      :svg="getIconSvg(item)"
                      aria-hidden="true"
                    />
                    <img
                      v-else-if="getIconSrc(item)"
                      class="c-workflow-rapid__process-icon"
                      :src="getIconSrc(item)"
                      :alt="getIconAlt(item)"
                      loading="lazy"
                    />
                    <span v-if="item.fields.label?.value" class="c-workflow-rapid__process-label">
                      {{ item.fields.label.value }}
                    </span>
                  </div>
                  <span
                    v-if="index < processItems.length - 1"
                    class="c-workflow-rapid__process-arrow"
                    aria-hidden="true"
                  ></span>
                </template>
              </div>
            </div>
          </article>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  WorkflowRapidProcessItemFields,
  WorkflowRapidProps,
  WorkflowRapidStepFields,
} from '@/types/components/WorkflowRapid';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

type WorkflowRapidStep = ScItem<WorkflowRapidStepFields> & { fields: WorkflowRapidStepFields };
type WorkflowRapidProcessItem = ScItem<WorkflowRapidProcessItemFields> & {
  fields: WorkflowRapidProcessItemFields;
};

const props = defineProps<WorkflowRapidProps>();
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-workflow-rapid'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const steps = computed<WorkflowRapidStep[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'steps');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowRapidStep => Boolean(item?.fields?.title?.value))
    : [];
});

const processItems = computed<WorkflowRapidProcessItem[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'processItems');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowRapidProcessItem => Boolean(item?.fields))
    : [];
});

const activeStepCount = computed(() => {
  const raw = Number(fields.value?.activeStepCount?.value ?? 0);
  return Number.isFinite(raw) ? Math.max(0, raw) : 0;
});

const timelineStyle = computed(() => {
  const count = Math.max(steps.value.length, 1);

  return {
    '--workflow-step-count': `${count}`,
    '--workflow-line-percent': `${50 / count}%`,
    '--workflow-line-tablet-offset': `${88 * (1 - 1 / count)}px`,
    '--workflow-line-desktop-offset': `${124 * (1 - 1 / count)}px`,
  };
});

const isStepActive = (step: WorkflowRapidStep, index: number) =>
  Boolean(step.fields.isActive?.value) || index < activeStepCount.value;

const getIconValue = (item: WorkflowRapidProcessItem) => {
  const icon = item.fields.icon?.value as
    | {
        src?: string;
        alt?: string;
        svgCode?: string;
        value?: {
          src?: string;
          alt?: string;
          svgCode?: string;
        };
      }
    | undefined;

  return icon?.value || icon || {};
};

const getIconSvg = (item: WorkflowRapidProcessItem) => getIconValue(item).svgCode || '';
const getIconSrc = (item: WorkflowRapidProcessItem) => getIconValue(item).src || '';
const getIconAlt = (item: WorkflowRapidProcessItem) =>
  getIconValue(item).alt || item.fields.label?.value || '';
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-workflow-rapid {
  padding: 42px 0 52px;
  background: $white;
  color: #111;

  svg {
    fill: transparent;
  }

  &__inner {
    width: 100%;
  }

  &__title {
    margin: 0;
    color: #3d3d3f;
    font-size: 24px;
    line-height: 1.18;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0;
  }

  &__timeline {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 36px;
    padding: 26px 22px;
    overflow: hidden;
    border-radius: 12px;
    background: #f4faf7;
  }

  &__timeline-line {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 32px;
    z-index: 0;
    width: 2px;
    background: #709879;
  }

  &__step {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 24px 1fr;
    gap: 16px;
    align-items: center;
  }

  &__dot {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #c9d9cd;
  }

  &__step--active &__dot {
    background: #6c9372;
  }

  &__step-title {
    margin: 0;
    color: #111;
    font-size: 15px;
    line-height: 1.18;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 32px;
  }

  &__info-card,
  &__process-card {
    min-width: 0;
    border: 1px solid #e4e7e5;
    border-radius: 12px;
    background: $white;
  }

  &__info-card {
    padding: 24px 20px 22px;
  }

  &__process-card {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 210px;
    padding: 24px 0;
  }

  &__card-title {
    margin: 0 0 18px;
    color: #111;
    font-size: 19px;
    line-height: 1.2;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0;
  }

  &__duration {
    display: flex;
    min-height: 68px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #eef4f0;
    color: #6c9372;
  }

  &__duration-value {
    font-size: 31px;
    line-height: 0.95;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__duration-unit {
    margin-top: 8px;
    color: #6a6a6a;
    font-size: 15px;
    line-height: 1;
    font-weight: 400;
  }

  &__bullets {
    margin-top: 22px;
    color: #666;
    font-size: 15px;
    line-height: 1.45;

    ul {
      display: grid;
      gap: 10px;
      margin: 0;
      padding-left: 20px;
    }

    li::marker {
      color: #111;
      font-size: 0.75em;
    }

    p {
      margin: 0;
    }
  }

  &__process-scroll {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
  }

  &__process-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    width: 560px;
    max-width: none;
    padding: 10px 42px 18px;
    box-sizing: border-box;
    flex: 0 0 auto;
  }

  &__process-item {
    display: flex;
    width: 96px;
    flex: 0 0 96px;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;
  }

  &__process-icon {
    display: block;
    width: 76px;
    height: 76px;
    overflow: visible;
    object-fit: contain;
  }

  &__process-icon :deep(svg) {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  &__process-label {
    min-height: 16px;
    color: #6c9372;
    font-size: 12px;
    line-height: 1.2;
    font-weight: 700;
  }

  &__process-arrow {
    position: relative;
    display: inline-block;
    width: 24px;
    height: 68px;
    flex: 0 0 24px;
    color: #6c9372;
  }

  &__process-arrow::before,
  &__process-arrow::after {
    content: '';
    position: absolute;
    top: 50%;
    display: block;
    background: currentColor;
  }

  &__process-arrow::before {
    left: 5px;
    width: 16px;
    height: 2px;
    transform: translateY(-50%);
  }

  &__process-arrow::after {
    right: 3px;
    width: 8px;
    height: 8px;
    border-top: 2px solid currentColor;
    border-right: 2px solid currentColor;
    background: transparent;
    transform: translateY(-50%) rotate(45deg);
  }

  @include tablet {
    padding: 52px 0 60px;

    &__title {
      font-size: 30px;
    }

    &__timeline {
      grid-template-columns: repeat(var(--workflow-step-count), minmax(0, 1fr));
      gap: 0;
      margin-top: 44px;
      padding: 42px 88px 32px;
      border-radius: 14px;
    }

    &__timeline-line {
      top: 56px;
      bottom: auto;
      left: 0;
      right: 0;
      width: 100%;
      height: 2px;
    }

    &__step {
      grid-template-columns: 1fr;
      justify-items: center;
      gap: 16px;
      align-items: start;
      text-align: center;
    }

    &__dot {
      width: 28px;
      height: 28px;
    }

    &__step-title {
      max-width: 160px;
      font-size: 15px;
    }

    &__cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 18px;
      margin-top: 32px;
    }

    &__info-card,
    &__process-card {
      border-radius: 14px;
    }

    &__info-card {
      padding: 28px 24px 24px;
    }

    &__process-card {
      min-height: 218px;
      padding: 32px 24px;
    }

    &__card-title {
      font-size: 18px;
    }

    &__duration-value {
      font-size: 30px;
    }

    &__process-scroll {
      overflow: visible;
    }

    &__process-row {
      width: 100%;
      min-width: 0;
      padding: 0;
      flex: 0 1 auto;
    }

    &__process-icon {
      width: 72px;
      height: 72px;
      fill: none;
    }

    &__process-arrow {
      height: 72px;
    }
  }

  @include tablet-landscape {
    padding: 58px 0 64px;

    &__title {
      font-size: 32px;
    }

    &__timeline {
      margin-top: 46px;
      padding: 42px 124px 32px;
      border-radius: 16px;
    }

    &__dot {
      width: 28px;
      height: 28px;
    }

    &__step-title {
      font-size: 15px;
    }

    &__cards {
      gap: 18px;
      margin-top: 36px;
    }

    &__info-card {
      padding: 28px 28px 26px;
    }

    &__process-card {
      min-height: 220px;
    }

    &__process-row {
      gap: 20px;
    }

    &__process-icon {
      width: 76px;
      height: 76px;
      fill: none;
    }

    &__process-arrow {
      height: 76px;
    }
  }
}
</style>
